import React, { useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import SearchTile from 'components/SearchKit/SearchTiles/SearchTile'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import Clickable from 'components/Common/Clickable'
import Label from 'LuxKit/Label/Label'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineGlobeAltIcon from 'LuxKit/Icons/line/LineGlobeAltIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPriceLoadingSkeleton from 'LuxKit/PricePoints/PriceRowPriceLoadingSkeleton'
import { useInView } from 'react-intersection-observer'
import * as Analytics from 'analytics/analytics'
import { promotionClickEvent, promotionViewEvent } from 'analytics/snowplow/events'
import { MALDIVES_CAMPAIGN_NAME, MALDIVES_MARKETING_BANNER_IMAGE_ID, MALDIVES_BUNDLE_DEFAULT_NIGHTS, MALDIVES_BUNDLE_LABEL } from 'constants/charterFlight'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LineCalendarCheckIcon from 'LuxKit/Icons/line/LineCalendarCheckIcon'
import LineClockWithTickIcon from 'LuxKit/Icons/line/LineClockWithTickIcon'

const StyledClickable = styled(Clickable)`
  margin-bottom: -${rem(16)};;

  ${mediaQueryUp.tablet} {
    margin-bottom: 0;
  }
`

interface Props {
  onViewBundlesClick: () => void
  price: number
  isPriceLoading?: boolean
}

const staticImages: Array<App.Image> = [
  {
    id: MALDIVES_MARKETING_BANNER_IMAGE_ID,
    title: 'Maldives luxury resort with overwater bungalows',
  },
]

const staticOffer: App.HotelOffer = {
  id: 'maldives-banner-static',
  type: 'hotel',
  parentType: 'hotel',
  name: 'All Inclusive Maldives Packages with Direct Flights and Transfers',
  slug: 'maldives-direct-banner',
  productType: 'hotel',
  location: 'Maldives',
  locations: ['Maldives'],
  saleUnit: 'package',
} as App.HotelOffer

function MaldivesBanner({ onViewBundlesClick, price, isPriceLoading = false }: Props) {
  // Intersection observer to track when banner comes into view
  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.5,
  })

  // Track promotion impression when banner comes into view
  useEffect(() => {
    if (inView) {
      Analytics.trackEvent(promotionViewEvent({
        promotionContext: {
          promotionName: MALDIVES_CAMPAIGN_NAME,
          promotionType: 'banner',
        },
      }))
    }
  }, [inView])

  // Track promotion click when customer clicks the banner
  const handleBannerClick = useCallback(() => {
    Analytics.trackEvent(promotionClickEvent({
      promotionContext: {
        promotionName: MALDIVES_CAMPAIGN_NAME,
        promotionType: 'banner',
      },
    }))
    onViewBundlesClick()
  }, [onViewBundlesClick])

  const features = (
    <Group direction="vertical" gap={4}>
      <BodyTextBlock variant="medium" startIcon={<LineGlobeAltIcon />}>
        Australia's only direct flight to the Maldives
      </BodyTextBlock>
      <BodyTextBlock variant="medium" startIcon={<LinePlaneDepartureIcon />}>
        Save up to 20 hours in transit
      </BodyTextBlock>
      <BodyTextBlock variant="medium" startIcon={<LineCalendarCheckIcon />}>
        Free cancellation within 7 days. Conditions apply.
      </BodyTextBlock>
      <BodyTextBlock variant="medium" startIcon={<LineClockWithTickIcon />}>
        Early check-in & late checkout
      </BodyTextBlock>
      <BodyTextBlock variant="medium" startIcon={<LineStarIcon />}>
        Luxury Escapes private charter aboard a full service A330-300 aircraft
      </BodyTextBlock>
    </Group>
  )

  const priceDisplay = isPriceLoading
    ? <PriceRowPriceLoadingSkeleton size="L" saleUnit />
    : <PriceRowPrice size="L" price={price} saleUnit="person" />

  const pricePoint = (
    <Group direction="vertical" gap={4}>
      <Group direction="horizontal" gap={4} verticalAlign="center">
        <LinePlaneDepartureIcon size="XS" colour="neutral-two" />
        <PriceRowPriceCaption>
          {MALDIVES_BUNDLE_DEFAULT_NIGHTS} nights + flights from
        </PriceRowPriceCaption>
      </Group>
      {priceDisplay}
    </Group>
  )

  return (
    <StyledClickable onClick={handleBannerClick}>
      <SearchTile
        ref={inViewRef}
        offer={staticOffer}
        images={staticImages}
        title="All Inclusive Maldives Packages with Direct Flights and Transfers"
        features={features}
        pricePoint={pricePoint}
        action={<TextButton nonInteractable kind="primary">View bundle offers</TextButton>}
        productLabel={<Label kind="primary" variant="maldives-charter">{MALDIVES_BUNDLE_LABEL}</Label>}
        background="highlight-primary"
      />
    </StyledClickable>
  )
}

export default MaldivesBanner
